/**
 * IntakeTokenRevealDialog — affichage **UNE FOIS** du token + URL après création.
 *
 * Sécurité (cadrage F2 §3.7 + parité api-keys.ts) :
 *   - Le token + URL **ne sont jamais persistés** côté frontend hors du `useState`
 *     local de l'arbre parent. Aucun `console.log`, aucun `localStorage`, aucun
 *     `sessionStorage`, aucun appel à des trackers tiers.
 *   - À la fermeture, le parent détruit le bundle (le composant ne le réexpose pas).
 *   - Le bouton « Copier » utilise `navigator.clipboard.writeText` (HTTPS only).
 *     Fallback `document.execCommand('copy')` pour dev local HTTP — silent fail
 *     si bloqué, on n'expose pas d'erreur (pas critique : le user peut sélectionner
 *     manuellement le texte).
 *
 * UX :
 *   - Le bouton « Fermer » est **secondary** tant que `Copier` n'a pas été
 *     cliqué (encourage à copier). Devient **primary** après copie.
 *   - aria-live="polite" annonce « Copié » au screen reader.
 *   - Backdrop click et Escape sont autorisés (le user peut volontairement
 *     fermer sans copier — sa responsabilité).
 *   - Warning explicite : « Ce lien ne sera plus affiché. »
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { IntakeLinkBundle } from "@kollectivia/shared";

type Props = {
  open: boolean;
  bundle: IntakeLinkBundle | null;
  onClose: () => void;
};

export function IntakeTokenRevealDialog({ open, bundle, onClose }: Props) {
  const { t } = useTranslation();
  const titleId = useId();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      return;
    }
    queueMicrotask(() => closeBtnRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open || !bundle) return null;

  const handleCopy = async () => {
    const url = bundle.intake_url;
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        return;
      }
    } catch {
      /* fall through to legacy fallback */
    }
    // Fallback HTTP local (dev) — silent fail si bloqué.
    try {
      const ta = document.createElement("textarea");
      ta.value = url;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
    } catch {
      /* silent — l'utilisateur peut sélectionner manuellement */
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="motion-safe:animate-fade-in relative z-10 w-full max-w-lg rounded-xl bg-white shadow-xl ring-1 ring-slate-200">
        <header className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <h2 id={titleId} className="text-base font-semibold text-slate-900">
              {t("intake_links.reveal.title")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("intake_links.reveal.subtitle")}</p>
          </div>
        </header>

        <div className="space-y-4 px-5 py-5">
          {/* Warning */}
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <svg
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
              className="mt-0.5 shrink-0"
            >
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10 6v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="10" cy="14" r="0.75" fill="currentColor" />
            </svg>
            <span>{t("intake_links.reveal.warning")}</span>
          </div>

          <a
            href={bundle.intake_url}
            target="_blank"
            rel="noopener noreferrer"
            referrerPolicy="no-referrer"
            className="text-brand-700 focus-visible:ring-brand-500 inline-block rounded text-sm font-medium underline focus-visible:outline-none focus-visible:ring-2"
          >
            {t("intake_links.reveal.open")}
          </a>
          {/* URL display */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              {t("intake_links.reveal.url_label")}
            </label>
            <div className="flex items-stretch gap-2">
              <code
                className="block flex-1 truncate rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800"
                translate="no"
              >
                {bundle.intake_url}
              </code>
              <button
                type="button"
                onClick={() => void handleCopy()}
                className="border-brand-600 bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 shrink-0 touch-manipulation rounded-md border px-3 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              >
                {t(copied ? "intake_links.reveal.copied" : "intake_links.reveal.copy")}
              </button>
            </div>
            <p
              aria-live="polite"
              role="status"
              className="mt-1 min-h-[1.25rem] text-xs text-emerald-700"
            >
              {copied ? t("intake_links.reveal.copied_announce") : ""}
            </p>
          </div>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className={[
              "focus-visible:ring-brand-500 touch-manipulation rounded-md px-4 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
              copied
                ? "bg-brand-600 hover:bg-brand-700 text-white"
                : "text-slate-700 hover:bg-slate-100",
            ].join(" ")}
          >
            {t("intake_links.reveal.close")}
          </button>
        </footer>
      </div>
    </div>
  );
}
